// components/profile/AddressInformation.tsx
import React, { useState, useEffect } from "react";
import {
  MapPin,
  Edit3,
  Save,
  X,
  Building2,
  Globe,
  Hash,
  Loader2,
  AlertCircle,
  CheckCircle2,
} from "lucide-react";

interface AddressInfo {
  address: string;
  city: string;
  state: string;
  country: string;
  zipCode: string;
}

interface AddressInformationProps {
  data: AddressInfo;
  onSave: (data: AddressInfo) => Promise<boolean>;
  isPending: boolean;
  isAnotherSectionEditing?: boolean;
  onEditingChange?: (isEditing: boolean) => void;
}

const AddressInformation: React.FC<AddressInformationProps> = ({
  data,
  onSave,
  isPending,
  isAnotherSectionEditing = false,
  onEditingChange,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<AddressInfo>(data);

  // Sync formData when data prop changes (external update)
  useEffect(() => {
    if (!isEditing) {
      setFormData(data);
    }
  }, [data, isEditing]);

  // Notify parent about editing state changes
  useEffect(() => {
    onEditingChange?.(isEditing);
  }, [isEditing, onEditingChange]);

  // Validation
  const isFormValid =
    formData.address.trim() !== "" &&
    formData.city.trim() !== "" &&
    formData.state.trim() !== "" &&
    formData.country.trim() !== "" &&
    formData.zipCode.trim() !== "";

  // Track changes
  const hasChanges =
    formData.address !== data.address ||
    formData.city !== data.city ||
    formData.state !== data.state ||
    formData.country !== data.country ||
    formData.zipCode !== data.zipCode;

  // Missing fields for validation feedback
  const missingFields: string[] = [];
  if (!formData.address.trim()) missingFields.push("Street Address");
  if (!formData.city.trim()) missingFields.push("City");
  if (!formData.state.trim()) missingFields.push("State/Province");
  if (!formData.country.trim()) missingFields.push("Country");
  if (!formData.zipCode.trim()) missingFields.push("ZIP/Postal Code");

  // Check if address is incomplete (for display purposes)
  const isAddressIncomplete =
    !data.address ||
    !data.city ||
    !data.state ||
    !data.country ||
    !data.zipCode;

  const handleChange = (field: keyof AddressInfo, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!isFormValid || isPending) return;
    const success = await onSave(formData);
    if (success) {
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setFormData(data);
    setIsEditing(false);
  };

  const handleStartEditing = () => {
    if (isAnotherSectionEditing) return;
    setIsEditing(true);
  };

  const fields: {
    key: keyof AddressInfo;
    label: string;
    icon: React.ElementType;
    placeholder: string;
    fullWidth?: boolean;
  }[] = [
    {
      key: "address",
      label: "Street Address",
      icon: MapPin,
      placeholder: "123 Main Street, Apt 4B",
      fullWidth: true,
    },
    {
      key: "city",
      label: "City",
      icon: Building2,
      placeholder: "New York",
    },
    {
      key: "state",
      label: "State/Province",
      icon: Globe,
      placeholder: "NY",
    },
    {
      key: "country",
      label: "Country",
      icon: Globe,
      placeholder: "United States",
    },
    {
      key: "zipCode",
      label: "ZIP/Postal Code",
      icon: Hash,
      placeholder: "10001",
    },
  ];

  return (
    <div
      id="address-information-section"
      className={`bg-white rounded-2xl shadow-sm border transition-all duration-300 ${
        isEditing
          ? "ring-2 ring-[#8B52FF] border-[#8B52FF]"
          : "border-gray-100 hover:shadow-md"
      }`}
    >
      {/* Header */}
      <div className="p-4 sm:p-6 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 sm:gap-4">
            <div
              className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center transition-all ${
                isEditing
                  ? "bg-gradient-to-br from-[#8B52FF] to-[#E05A6A]"
                  : "bg-gradient-to-br from-[#8B52FF]/10 to-[#E05A6A]/10"
              }`}
            >
              <MapPin
                className={`w-5 h-5 sm:w-6 sm:h-6 ${
                  isEditing ? "text-white" : "text-[#8B52FF]"
                }`}
              />
            </div>
            <div>
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
                Address Information
              </h2>
              <p className="text-xs sm:text-sm text-gray-500">
                Your residential address
              </p>
            </div>
          </div>

          {/* Desktop Edit Button */}
          {!isEditing && (
            <button
              onClick={handleStartEditing}
              disabled={isAnotherSectionEditing}
              className={`hidden sm:flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-all ${
                isAnotherSectionEditing
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              <Edit3 className="w-4 h-4" />
              Edit
            </button>
          )}
        </div>

        {/* Editing Mode Banner */}
        {isEditing && (
          <div className="mt-4 p-3 bg-[#8B52FF]/5 border border-[#8B52FF]/20 rounded-xl">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-[#8B52FF] rounded-full animate-pulse" />
              <span className="text-sm font-medium text-[#8B52FF]">
                Editing Mode
              </span>
            </div>
          </div>
        )}

        {/* Incomplete Address Warning (when not editing) */}
        {!isEditing && isAddressIncomplete && (
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-xl">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-amber-800">
                  Address Incomplete
                </p>
                <p className="text-xs text-amber-600 mt-0.5">
                  Please complete your address for verification
                </p>
              </div>
              <button
                onClick={handleStartEditing}
                disabled={isAnotherSectionEditing}
                className="text-sm font-medium text-amber-700 hover:text-amber-800 underline"
              >
                Complete Now
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Form Content */}
      <div className="p-4 sm:p-6">
        {/* Mobile Edit Button */}
        {!isEditing && (
          <button
            onClick={handleStartEditing}
            disabled={isAnotherSectionEditing}
            className={`sm:hidden w-full mb-4 flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-medium transition-all ${
              isAnotherSectionEditing
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            <Edit3 className="w-4 h-4" />
            Edit Address
          </button>
        )}

        {/* Fields Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
          {fields.map((field) => {
            const Icon = field.icon;
            const value = formData[field.key];
            const isEmpty = !value?.trim();
            const isRequired = true;

            return (
              <div
                key={field.key}
                className={field.fullWidth ? "sm:col-span-2" : ""}
              >
                <label className="block text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                  {field.label}
                  {isRequired && <span className="text-red-500 ml-1">*</span>}
                </label>

                {isEditing ? (
                  <div className="relative">
                    <div className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 pointer-events-none">
                      <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      value={value}
                      onChange={(e) => handleChange(field.key, e.target.value)}
                      placeholder={field.placeholder}
                      disabled={isPending}
                      className={`w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-2.5 sm:py-3 text-sm sm:text-base border-2 rounded-xl transition-all outline-none disabled:opacity-50 ${
                        isEmpty
                          ? "border-red-200 focus:border-red-400 focus:ring-2 focus:ring-red-100"
                          : "border-gray-200 focus:border-[#8B52FF] focus:ring-2 focus:ring-[#8B52FF]/20"
                      }`}
                    />
                    {isEmpty && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <AlertCircle className="w-4 h-4 text-red-400" />
                      </div>
                    )}
                  </div>
                ) : (
                  <div
                    className={`flex items-center gap-3 p-3 sm:p-4 rounded-xl border ${
                      isEmpty
                        ? "bg-gray-50 border-gray-200"
                        : "bg-gray-50 border-gray-100"
                    }`}
                  >
                    <Icon
                      className={`w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 ${
                        isEmpty ? "text-gray-300" : "text-gray-400"
                      }`}
                    />
                    <span
                      className={`text-sm sm:text-base ${
                        isEmpty ? "text-gray-400 italic" : "text-gray-900"
                      }`}
                    >
                      {value || "Not set"}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Inline Validation Messages (when editing) */}
        {isEditing && missingFields.length > 0 && (
          <div className="mt-4 p-3 bg-red-50 border border-red-100 rounded-xl">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-700">
                  Required fields missing:
                </p>
                <p className="text-xs text-red-600 mt-1">
                  {missingFields.join(", ")}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons (Attached to Component) */}
        {isEditing && (
          <div className="mt-6 pt-4 border-t border-gray-100">
            {/* Change Status Indicator */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                {hasChanges ? (
                  <>
                    <div className="w-2 h-2 bg-amber-500 rounded-full" />
                    <span className="text-sm text-amber-600 font-medium">
                      Unsaved changes
                    </span>
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    <span className="text-sm text-green-600">
                      No changes made
                    </span>
                  </>
                )}
              </div>

              <div className="text-xs text-gray-500">
                {5 - missingFields.length}/5 fields complete
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleCancel}
                disabled={isPending}
                className="flex-1 sm:flex-none px-6 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <X className="w-4 h-4" />
                Cancel
              </button>

              <button
                onClick={handleSave}
                disabled={!isFormValid || isPending || !hasChanges}
                className={`flex-1 px-6 py-3 rounded-xl font-medium transition-all flex items-center justify-center gap-2 ${
                  isFormValid && hasChanges && !isPending
                    ? "bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] text-white hover:opacity-90 shadow-lg shadow-[#8B52FF]/25"
                    : "bg-gray-100 text-gray-400 cursor-not-allowed"
                }`}
              >
                {isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Save Address
                  </>
                )}
              </button>
            </div>

            {/* Helper Text */}
            {!hasChanges && (
              <p className="text-xs text-gray-500 text-center mt-3">
                Make changes to enable the save button
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AddressInformation;
